import { useCallback, useEffect, useState } from "react";
import {
  type ArtistPattern,
  type PatternsPage,
  artistLine,
  fetchPatterns,
  hiddenBlockedCount,
  keywordLabel,
  overrideKeyword,
  summaryLine,
} from "../lib/patternsPage";
import { formatPacificDate, formatPrice } from "../lib/format";

/**
 * The learned dismiss patterns, ported from `server/pages/patterns.py`.
 *
 * The blocked listings and the source dismissals sit behind collapsible
 * sections: an artist with 50 blocked listings would otherwise bury the next
 * artist off the bottom of the page. The keyword chips
 * stay open, because they are the thing you came to check.
 */

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <details className="text-xs">
      <summary className="cursor-pointer text-neutral-400 hover:text-neutral-200">{title}</summary>
      <div className="pt-2">{children}</div>
    </details>
  );
}

function Artist({
  artist,
  onOverride,
  busy,
}: {
  artist: ArtistPattern;
  onOverride: (artist: string, keyword: string) => void;
  busy: string | null;
}) {
  const active = artist.keywords.filter((k) => !k.overridden);
  const moreBlocked = hiddenBlockedCount(artist);

  return (
    <li className="space-y-3 rounded-md border border-neutral-800 bg-neutral-900 p-4">
      <div>
        <h2 className={artist.dormant ? "text-sm font-medium text-neutral-400" : "font-semibold"}>
          {artist.artist}
        </h2>
        <p className="text-xs text-neutral-500">{artistLine(artist)}</p>
      </div>

      <ul className="flex flex-wrap gap-2">
        {artist.keywords.map((k) => (
          <li
            key={k.keyword}
            className={
              k.overridden
                ? "rounded border border-neutral-800 px-2 py-0.5 font-mono text-xs text-neutral-600 line-through"
                : "rounded border border-neutral-700 px-2 py-0.5 font-mono text-xs text-neutral-300"
            }
          >
            {keywordLabel(k)}
          </li>
        ))}
      </ul>

      {artist.blocked_count > 0 ? (
        <Section title={`Currently-blocked listings (${artist.blocked_count})`}>
          <ul className="space-y-1">
            {artist.blocked.map((b) => {
              const price = formatPrice(b.price);
              const at = formatPacificDate(b.filtered_at);
              return (
                <li key={b.key} className="flex flex-wrap gap-x-2 text-neutral-400">
                  {b.url ? (
                    <a
                      href={b.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-neutral-300 underline hover:text-white"
                    >
                      {b.title}
                    </a>
                  ) : (
                    <span className="text-neutral-300">{b.title}</span>
                  )}
                  <span className="font-mono text-neutral-600">{b.matched_pattern}</span>
                  {price ? <span>{price}</span> : null}
                  {at ? <span className="text-neutral-600">{at}</span> : null}
                </li>
              );
            })}
          </ul>
          {moreBlocked > 0 ? (
            <p className="pt-1 text-neutral-600">+{moreBlocked} more not shown</p>
          ) : null}
        </Section>
      ) : null}

      <Section title="Source dismissals that taught this pattern">
        {artist.example_titles.length > 0 ? (
          <ul className="space-y-0.5 text-neutral-400">
            {artist.example_titles.map((t) => (
              <li key={t}>{t}</li>
            ))}
          </ul>
        ) : (
          <p className="text-neutral-600">(no example titles stored)</p>
        )}
      </Section>

      {active.length > 0 ? (
        <Section title="Fix a keyword">
          <p className="pb-2 text-neutral-500">
            Marking a keyword wrong drops it from the pattern and releases the listings it was
            hiding, straight away.
          </p>
          <div className="flex flex-wrap gap-2">
            {active.map((k) => (
              <button
                key={k.keyword}
                disabled={busy !== null}
                onClick={() => onOverride(artist.artist, k.keyword)}
                className="rounded border border-neutral-700 px-2 py-1 font-mono text-neutral-300 hover:border-red-800 hover:text-red-300 disabled:opacity-40"
              >
                ✗ {k.keyword}
              </button>
            ))}
          </div>
        </Section>
      ) : null}
    </li>
  );
}

export default function Patterns() {
  const [page, setPage] = useState<PatternsPage | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  const load = useCallback(() => {
    setError(null);
    fetchPatterns()
      .then(setPage)
      .catch((err: unknown) => setError(err instanceof Error ? err.message : String(err)));
  }, []);

  useEffect(load, [load]);

  const onOverride = useCallback((artist: string, keyword: string) => {
    setActionError(null);
    setBusy(`${artist}:${keyword}`);
    // NOT optimistic, unlike the other pages. An override re-derives the whole
    // artist, and the keyword usually leaves the list entirely rather than
    // gaining a strikethrough -- and it can take the whole pattern with it.
    // Guessing at that locally would show a state the server never produced.
    overrideKeyword(artist, keyword)
      .then(setPage)
      .catch((err: unknown) =>
        setActionError(err instanceof Error ? err.message : String(err)),
      )
      .finally(() => setBusy(null));
  }, []);

  if (error) {
    return (
      <section className="space-y-3">
        <h1 className="text-xl font-semibold">Patterns</h1>
        <div className="rounded-md border border-red-900 bg-red-950/40 p-4 text-sm">{error}</div>
        <button
          onClick={load}
          className="rounded-md bg-neutral-100 px-4 py-2 text-sm text-neutral-900 hover:bg-white"
        >
          Try again
        </button>
      </section>
    );
  }

  if (page === null) {
    return (
      <section className="space-y-3">
        <h1 className="text-xl font-semibold">Patterns</h1>
        <p className="text-sm text-neutral-400">Loading…</p>
      </section>
    );
  }

  return (
    <section className="space-y-4">
      <div>
        <h1 className="text-xl font-semibold">Patterns</h1>
        <p className="text-sm text-neutral-400">
          Per-artist keywords learned from your dismissals. Anything matching is hidden from Browse
          automatically.
        </p>
      </div>

      {actionError ? (
        <div className="rounded-md border border-amber-900 bg-amber-950/40 p-3 text-sm">
          {actionError}
        </div>
      ) : null}

      {page.artists.length === 0 ? (
        <p className="text-sm text-neutral-400">
          No learned patterns yet. Dismiss two or more listings from the same artist to generate one.
        </p>
      ) : (
        <>
          <p className="text-sm text-neutral-500">{summaryLine(page)}</p>
          <ul className="space-y-3">
            {page.artists.map((artist) => (
              <Artist
                key={artist.artist}
                artist={artist}
                onOverride={onOverride}
                busy={busy}
              />
            ))}
          </ul>
        </>
      )}
    </section>
  );
}
